import { langueCourante } from './langue'
/**
 * Client HTTP de l’application — le seul endroit qui parle à l’API.
 *
 * Ce qu’il garantit (ARCHITECTURE §4, §5, §9.4) :
 *
 *  - **cookies de session envoyés** (`credentials: 'include'`) : `tb_staff` ou
 *    `tb_abonne`, jamais de jeton en `localStorage` ;
 *  - **CSRF double-submit** : l’en-tête `X-CSRF-Token` est recopié depuis le
 *    cookie non-HttpOnly `tb_csrf` sur **toute mutation** ;
 *  - **erreurs métier lisibles** : l’enveloppe `{ erreur: { code, message,
 *    details } }` devient une `ApiError`, et c’est `message` — écrit par le
 *    backend pour le personnel du théâtre — que l’interface affiche. Jamais
 *    « une erreur est survenue » sur un 409.
 *
 * Aucune URL absolue : tout part de `/api`, proxifié par Vite en développement
 * et servi par le même hôte en production.
 */

import { estEnveloppeErreur, type CodeErreur, type DetailsErreur } from '@tb/shared'

/* -------------------------------------------------------------------------- */
/* Constantes                                                                 */
/* -------------------------------------------------------------------------- */

/** Préfixe de toutes les routes (ARCHITECTURE §8). */
export const BASE_API: string = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api'

/** Cookie non-HttpOnly déposé par le backend, lisible en JS (ARCHITECTURE §4). */
export const COOKIE_CSRF = 'tb_csrf'

/** En-tête exigé par le backend sur toute mutation. */
export const ENTETE_CSRF = 'X-CSRF-Token'

const METHODES_SANS_CSRF: ReadonlySet<string> = new Set(['GET', 'HEAD', 'OPTIONS'])

/* -------------------------------------------------------------------------- */
/* Erreurs                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Erreur renvoyée par l’API, dans le format uniforme d’ARCHITECTURE §5.
 *
 * `message` est destiné à être affiché **tel quel** : c’est un message métier
 * (« Le siège F12 est déjà occupé pour cette représentation. »).
 */
export class ApiError extends Error {
  readonly code: CodeErreur
  /**
   * Le code tel que le serveur l’a écrit, y compris quand il n’appartient pas
   * à `CODE_ERREUR` — le vocabulaire d’exploitation de la plateforme
   * (`ETAT_INVALIDE`, `SLUG_RESERVE`, `TENANT_INTROUVABLE`…) vit dans
   * `backend/plateforme/erreurs.ts` et ne traverse pas le contrat métier.
   * `code` reste, lui, dans l’enum : les écrans qui l’aiguillent ne changent pas.
   */
  readonly codeBrut: string
  readonly details: DetailsErreur | undefined
  /** Statut HTTP renvoyé (409 conflit métier, 403 rôle, 422 schéma…). */
  readonly http: number

  constructor(
    code: CodeErreur,
    message: string,
    http: number,
    details?: DetailsErreur,
    codeBrut?: string,
  ) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.codeBrut = codeBrut ?? code
    this.http = http
    this.details = details
    Object.setPrototypeOf(this, ApiError.prototype)
  }

  /** 409 : un invariant métier s’oppose à l’opération (I-01, I-03, TAR-04…). */
  get estConflitMetier(): boolean {
    return this.http === 409
  }

  /** 401 : pas (ou plus) de session. L’interface renvoie vers la connexion. */
  get estNonAuthentifie(): boolean {
    return this.http === 401
  }

  /** 403 : session valide mais rôle insuffisant, ou CSRF rejeté. */
  get estInterdit(): boolean {
    return this.http === 403
  }

  /** 404 : la ressource n’existe pas (ou plus). */
  get estIntrouvable(): boolean {
    return this.http === 404
  }

  /** 400 / 422 : données refusées. `details.champs` liste les erreurs de saisie. */
  get estValidation(): boolean {
    return this.code === 'VALIDATION' && (this.http === 400 || this.http === 422)
  }

  /**
   * Construit l’erreur depuis le corps d’une réponse non-2xx.
   *
   * ★★ DEUX VOCABULAIRES D’ERREUR VOYAGENT SUR CETTE MÊME ENVELOPPE, et n’en
   *    reconnaître qu’un revenait à JETER le message de l’autre.
   *
   *    `estEnveloppeErreur` valide le `code` contre `CODE_ERREUR`, l’enum du
   *    contrat **métier**. La plateforme, elle, répond avec le sien
   *    (`CODE_ERREUR_PLATEFORME` : `ETAT_INVALIDE`, `SLUG_RESERVE`…) sur
   *    exactement la même forme `{ erreur: { code, message, details } }`.
   *    Le contrôle échouait donc pour toute la console, et l’écran affichait
   *    « La requête a été refusée par le serveur. » là où le backend avait
   *    écrit « Vous ne pouvez pas retirer votre propre accès. » ou « Le délai
   *    de grâce de « mailpit » court jusqu’au 2026-09-29. »
   *
   *    C’est l’exact contraire de la règle du produit (« les 409 affichent le
   *    message métier du backend, jamais “une erreur est survenue” »), et ça
   *    ne se voyait nulle part : la requête, elle, était correctement refusée.
   *
   *    On accepte donc toute enveloppe qui porte un `message` utilisable. Le
   *    `code` typé retombe sur `VALIDATION` quand il est hors enum — aucun
   *    écran n’aiguille sur un code de plateforme — et `codeBrut` conserve la
   *    valeur exacte pour qui en aurait besoin.
   */
  static depuisCorps(corps: unknown, http: number): ApiError {
    if (estEnveloppeErreur(corps)) {
      const { code, message, details } = corps.erreur
      return new ApiError(code, message, http, details)
    }
    const etrangere = enveloppeEtrangere(corps)
    if (etrangere !== null) {
      return new ApiError('VALIDATION', etrangere.message, http, etrangere.details, etrangere.code)
    }
    return new ApiError('VALIDATION', messageParDefaut(http), http)
  }
}

/**
 * Le serveur n’a pas répondu : coupure réseau, API arrêtée, requête annulée par
 * le navigateur. Distincte d'`ApiError` — il n’y a ni code métier ni statut.
 * Le cas est réel pour l’app de scan : le hall n’a pas toujours du réseau (SCAN-06).
 */
export class ErreurReseau extends Error {
  override readonly cause: unknown

  constructor(cause: unknown) {
    super('Le serveur est injoignable. Vérifiez la connexion, puis réessayez.')
    this.name = 'ErreurReseau'
    this.cause = cause
    Object.setPrototypeOf(this, ErreurReseau.prototype)
  }
}

/**
 * Une enveloppe `{ erreur: { code, message, details } }` dont le `code` n’est
 * pas du contrat métier — celui de la plateforme, en pratique. `null` si le
 * corps n’a rien d’affichable : une page HTML de proxy, un 502, un corps vide.
 *
 * Le `message` est exigé non vide : un message absent vaut mieux remplacé par
 * la phrase par défaut, qui est au moins écrite pour être lue.
 */
function enveloppeEtrangere(
  valeur: unknown,
): { code: string; message: string; details: DetailsErreur | undefined } | null {
  if (typeof valeur !== 'object' || valeur === null) return null
  const erreur = (valeur as { erreur?: unknown }).erreur
  if (typeof erreur !== 'object' || erreur === null) return null
  const { code, message, details } = erreur as {
    code?: unknown
    message?: unknown
    details?: unknown
  }
  if (typeof message !== 'string' || message.trim() === '') return null
  return {
    code: typeof code === 'string' ? code : 'INCONNU',
    message,
    details: (details ?? undefined) as DetailsErreur | undefined,
  }
}

function messageParDefaut(http: number): string {
  if (http === 401) return 'Session expirée, veuillez vous reconnecter.'
  if (http === 403) return 'Vous n’avez pas les droits nécessaires pour cette action.'
  if (http === 404) return 'Ressource introuvable.'
  if (http >= 500) return 'Le serveur a rencontré une erreur. L’incident est journalisé.'
  return 'La requête a été refusée par le serveur.'
}

/** Garde de type : `if (estApiError(e) && e.estConflitMetier) …` */
export function estApiError(valeur: unknown): valeur is ApiError {
  return valeur instanceof ApiError
}

/**
 * Message affichable pour n’importe quelle erreur attrapée.
 * C’est ce que consomment les toasts et les bandeaux d’erreur.
 */
export function messageErreur(erreur: unknown, repli = 'Opération impossible.'): string {
  if (erreur instanceof ApiError || erreur instanceof ErreurReseau) return erreur.message
  if (erreur instanceof Error && erreur.message !== '') return erreur.message
  return repli
}

/* -------------------------------------------------------------------------- */
/* Session expirée — notification globale                                     */
/* -------------------------------------------------------------------------- */

type EcouteurSession = () => void
const ecouteursSession = new Set<EcouteurSession>()

/**
 * S’abonner aux 401 rencontrés par n’importe quelle requête.
 * `AuthProvider` s’en sert pour invalider la session et renvoyer vers l’écran
 * de connexion sans que chaque écran ait à s’en soucier.
 *
 * @returns la fonction de désabonnement.
 */
export function surSessionExpiree(ecouteur: EcouteurSession): () => void {
  ecouteursSession.add(ecouteur)
  return () => {
    ecouteursSession.delete(ecouteur)
  }
}

function signalerSessionExpiree(): void {
  for (const ecouteur of ecouteursSession) ecouteur()
}

/* -------------------------------------------------------------------------- */
/* Cookies                                                                    */
/* -------------------------------------------------------------------------- */

/** Lit un cookie non-HttpOnly. `null` s’il est absent. */
export function lireCookie(nom: string): string | null {
  if (typeof document === 'undefined') return null
  const prefixe = `${nom}=`
  for (const morceau of document.cookie.split(';')) {
    const cookie = morceau.trim()
    if (cookie.startsWith(prefixe)) {
      return decodeURIComponent(cookie.slice(prefixe.length))
    }
  }
  return null
}

/* -------------------------------------------------------------------------- */
/* Construction des URL                                                       */
/* -------------------------------------------------------------------------- */

export type ValeurQuery =
  string | number | boolean | null | undefined | readonly (string | number)[]

export type ParamsQuery = Readonly<Record<string, ValeurQuery>>

/**
 * `/souscriptions` + `{ page: 2, statut: ['confirmee'] }`
 *   → `/api/souscriptions?page=2&statut=confirmee`
 *
 * Les valeurs `null` / `undefined` sont omises : un filtre vide ne doit pas
 * arriver au backend sous forme de chaîne « undefined ».
 */
export function construireUrl(chemin: string, query?: ParamsQuery): string {
  const base = chemin.startsWith('/') ? `${BASE_API}${chemin}` : `${BASE_API}/${chemin}`
  if (query === undefined) return base

  const parametres = new URLSearchParams()
  for (const [cle, valeur] of Object.entries(query)) {
    if (valeur === null || valeur === undefined) continue
    if (Array.isArray(valeur)) {
      for (const element of valeur) parametres.append(cle, String(element))
    } else {
      parametres.append(cle, String(valeur))
    }
  }
  const chaine = parametres.toString()
  return chaine === '' ? base : `${base}?${chaine}`
}

/* -------------------------------------------------------------------------- */
/* Requête                                                                    */
/* -------------------------------------------------------------------------- */

/** Ce que l’on attend en retour. `vide` ignore le corps (204, redirections). */
export type TypeReponse = 'json' | 'texte' | 'blob' | 'vide'

export interface OptionsRequete {
  /** Paramètres de query string. */
  readonly query?: ParamsQuery | undefined
  /** Annulation (TanStack Query fournit le sien). */
  readonly signal?: AbortSignal | undefined
  /** En-têtes additionnels. `Content-Type` et CSRF sont gérés ici. */
  readonly entetes?: Readonly<Record<string, string>> | undefined
  /** Type de réponse attendu. Défaut : `json`. */
  readonly reponse?: TypeReponse | undefined
}

interface OptionsInternes extends OptionsRequete {
  readonly corps?: unknown
}

/** Une seule réponse initialise le cookie, avant les lectures parallèles. */
let amorcageCsrf: Promise<void> | undefined

async function preparerCookieCsrf(methode: string): Promise<() => void> {
  while (amorcageCsrf !== undefined) await amorcageCsrf
  if (lireCookie(COOKIE_CSRF) !== null) return () => undefined

  let terminer: () => void = () => undefined
  const attente = new Promise<void>((resolve) => {
    terminer = resolve
  })
  amorcageCsrf = attente
  const liberer = () => {
    if (amorcageCsrf === attente) amorcageCsrf = undefined
    terminer()
  }
  if (METHODES_SANS_CSRF.has(methode)) return liberer

  // Une mutation peut aussi être le premier appel. La lecture sans session
  // pose le cookie même avec401 ; aucun refus de mutation n’est rejoué.
  try {
    await fetch(construireUrl('/auth/moi'), {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    })
  } catch (cause) {
    throw new ErreurReseau(cause)
  } finally {
    liberer()
  }
  return () => undefined
}

async function requete<T>(
  methode: string,
  chemin: string,
  options: OptionsInternes = {},
): Promise<T> {
  const entetes = new Headers({ Accept: 'application/json' })
  if (/^\/(?:boutique|portail|auth\/(?:abonne|lien-magique))(?:\/|$)/u.test(chemin))
    entetes.set('Accept-Language', langueCourante())
  for (const [cle, valeur] of Object.entries(options.entetes ?? {})) {
    entetes.set(cle, valeur)
  }

  // Corps : JSON par défaut, FormData tel quel (imports IMP-01, logo CFG-01).
  let corps: BodyInit | undefined
  if (options.corps instanceof FormData) {
    corps = options.corps
  } else if (options.corps !== undefined) {
    corps = JSON.stringify(options.corps)
    entetes.set('Content-Type', 'application/json')
  }

  const init: RequestInit = {
    method: methode,
    headers: entetes,
    // Le cookie de session voyage avec chaque appel ; aucun jeton côté JS.
    credentials: 'include',
    // Une réponse d’API ne se met jamais en cache navigateur : TanStack Query
    // s’occupe du cache applicatif, lui seul sait quand invalider.
    cache: 'no-store',
  }
  if (corps !== undefined) init.body = corps
  if (options.signal !== undefined) init.signal = options.signal

  const terminerAmorcage = await preparerCookieCsrf(methode)
  let reponse: Response
  try {
    // Relire le cookie après l’amorçage, juste avant l’envoi de la mutation.
    if (!METHODES_SANS_CSRF.has(methode)) {
      const jeton = lireCookie(COOKIE_CSRF)
      if (jeton !== null) entetes.set(ENTETE_CSRF, jeton)
    }
    reponse = await fetch(construireUrl(chemin, options.query), init)
  } catch (cause) {
    // Une annulation volontaire n’est pas une panne : on la laisse remonter
    // telle quelle pour que TanStack Query la reconnaisse.
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new ErreurReseau(cause)
  } finally {
    terminerAmorcage()
  }

  if (!reponse.ok) {
    const erreur = ApiError.depuisCorps(await lireCorpsErreur(reponse), reponse.status)
    // Les routes d’authentification gèrent leur propre 401 (« pas de session »
    // est une réponse normale pour `GET /auth/moi`).
    // Les refus attendus sur les formulaires de connexion ne révoquent pas
    // une session staff. En particulier, une erreur de connexion console ne
    // doit pas vider ses propres requêtes et effacer le message du formulaire.
    if (
      erreur.estNonAuthentifie &&
      !chemin.startsWith('/auth/') &&
      !chemin.startsWith('/console/auth/')
    ) {
      signalerSessionExpiree()
    }
    throw erreur
  }

  return (await lireCorps(reponse, options.reponse ?? 'json')) as T
}

async function lireCorpsErreur(reponse: Response): Promise<unknown> {
  const type = reponse.headers.get('Content-Type') ?? ''
  try {
    if (type.includes('application/json')) return await reponse.json()
    // Une page d’erreur HTML (proxy, 502) n’a rien à dire à l’utilisateur.
    await reponse.text()
    return null
  } catch {
    return null
  }
}

async function lireCorps(reponse: Response, type: TypeReponse): Promise<unknown> {
  if (type === 'vide' || reponse.status === 204) return undefined
  if (type === 'blob') return await reponse.blob()
  if (type === 'texte') return await reponse.text()

  const texte = await reponse.text()
  if (texte === '') return undefined
  try {
    return JSON.parse(texte)
  } catch {
    throw new ApiError('VALIDATION', 'Réponse du serveur illisible.', reponse.status)
  }
}

/* -------------------------------------------------------------------------- */
/* Verbes                                                                     */
/* -------------------------------------------------------------------------- */

export const api = {
  get: <T>(chemin: string, options?: OptionsRequete): Promise<T> =>
    requete<T>('GET', chemin, options ?? {}),

  post: <T>(chemin: string, corps?: unknown, options?: OptionsRequete): Promise<T> =>
    requete<T>('POST', chemin, { ...(options ?? {}), corps }),

  put: <T>(chemin: string, corps?: unknown, options?: OptionsRequete): Promise<T> =>
    requete<T>('PUT', chemin, { ...(options ?? {}), corps }),

  patch: <T>(chemin: string, corps?: unknown, options?: OptionsRequete): Promise<T> =>
    requete<T>('PATCH', chemin, { ...(options ?? {}), corps }),

  /** `delete` est un mot réservé : la fonction s’appelle `del`. */
  del: <T>(chemin: string, options?: OptionsRequete): Promise<T> =>
    requete<T>('DELETE', chemin, options ?? {}),

  /** Récupère un PDF (carte, billets, émargement — DOC-05, SCAN-06). */
  blob: (chemin: string, options?: OptionsRequete): Promise<Blob> =>
    requete<Blob>('GET', chemin, { ...(options ?? {}), reponse: 'blob' }),

  /** Comme `blob`, pour les routes qui renvoient un PDF en réponse d’un `POST`
   * (ex. `POST /cartes/:id/regenerer`, DOC-04). */
  postBlob: (chemin: string, corps?: unknown, options?: OptionsRequete): Promise<Blob> =>
    requete<Blob>('POST', chemin, { ...(options ?? {}), corps, reponse: 'blob' }),
} as const

export const { get, post, put, patch, del } = api

/* -------------------------------------------------------------------------- */
/* Téléchargement d’un document                                               */
/* -------------------------------------------------------------------------- */

/**
 * Télécharge un PDF de l’API et déclenche l’enregistrement côté navigateur.
 * Passe par `fetch` (et non par un simple lien) pour que le cookie de session
 * et les erreurs métier soient traités comme partout ailleurs.
 *
 * ```ts
 * await telecharger(`/cartes/${id}.pdf`, `carte-${nom}.pdf`)
 * ```
 */
export async function telecharger(chemin: string, nomFichier: string): Promise<void> {
  const blob = await api.blob(chemin)
  declencherTelechargement(blob, nomFichier)
}

/**
 * Comme `telecharger`, pour une route qui produit son PDF via un `POST`
 * (ex. régénération de carte — DOC-04, l’action elle-même crée le document).
 */
export async function telechargerDepuisPost(
  chemin: string,
  nomFichier: string,
  corps?: unknown,
): Promise<void> {
  const blob = await api.postBlob(chemin, corps)
  declencherTelechargement(blob, nomFichier)
}

function declencherTelechargement(blob: Blob, nomFichier: string): void {
  const url = URL.createObjectURL(blob)
  try {
    const lien = document.createElement('a')
    lien.href = url
    lien.download = nomFichier
    lien.rel = 'noopener'
    document.body.append(lien)
    lien.click()
    lien.remove()
  } finally {
    // Laisse au navigateur le temps de démarrer le téléchargement.
    setTimeout(() => {
      URL.revokeObjectURL(url)
    }, 10_000)
  }
}

/** Ouvre un PDF dans un nouvel onglet (impression au guichet — VEN-05). */
export async function ouvrirDocument(chemin: string): Promise<void> {
  const blob = await api.blob(chemin)
  const url = URL.createObjectURL(blob)
  const fenetre = window.open(url, '_blank', 'noopener')
  if (fenetre === null) {
    // Bloqueur de fenêtres : on retombe sur un téléchargement classique.
    const lien = document.createElement('a')
    lien.href = url
    lien.download = ''
    lien.click()
  }
  setTimeout(() => {
    URL.revokeObjectURL(url)
  }, 60_000)
}
